import { Button, Container, Group, Skeleton, Stack, Title, useMatches } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Carousel, MakeCard } from "@/components/elements";
import { useMarketMakes } from "@/services/market/get-makes";

import type { Make, TopMake } from "@/types/make";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type MakesCarouselProps = {
  to: string;
  makes?: Make[] | TopMake[];
} & MantineStyleProps;

export const MakesCarousel: FC<MakesCarouselProps> = ({ to, makes, ...props }) => {
  const { t } = useTranslation();
  const { data = [], isLoading } = useMarketMakes();

  const carouselMakes = makes ?? data;
  const slidesCount = carouselMakes.length;

  const hasIndicators = useMatches({
    base: slidesCount > 4,
    sm: slidesCount > 5,
    md: slidesCount > 7,
  });

  return (
    <Stack gap="md" {...props}>
      <Group justify="space-between">
        <Title order={4}>{t("market.top_makes")}</Title>
        <Button
          variant="filled"
          maw={180}
          component={Link}
          to={to}
          aria-label={t("market.view_all_makes") || ""}
        >
          {t("market.view_all_makes")}
        </Button>
      </Group>
      <Container size="xl" style={{ overflow: "hidden" }} p={2} mx={0}>
        <Skeleton visible={!makes && isLoading}>
          <Carousel
            slideSize={{ base: "15%", xs: "20%", md: "calc(100% / 6.7)" }}
            slides={carouselMakes.map((make) => (
              <MakeCard hideBG key={make.id} id={make.id} showMake make={make.name} marketCard />
            ))}
            styles={{ viewport: { overflow: "visible" } }}
            withIndicators={hasIndicators}
            pb={hasIndicators ? "xl" : 0}
          />
        </Skeleton>
      </Container>
    </Stack>
  );
};
